/**
 * Generic HTTP transport.
 *
 * Owns only cross-cutting transport concerns: base URL, default headers,
 * per-request correlation ID, active locale propagation, the
 * Authorization header, the `x-business-id` hint, and the 401 →
 * refresh-once → retry flow. Feature API services
 * (`features/*\/api/*.service.ts`) own resource-specific calls.
 *
 * Phase 3.1 changes:
 *   - `withCredentials: true` so the HttpOnly `invento_refresh` cookie
 *     is sent on `/auth/*` requests. Other endpoints stay Bearer-only
 *     because the backend cookie's `Path=/api/v1/auth` keeps it
 *     scoped — the browser doesn't attach it to non-auth routes.
 *   - 401 retry no longer reads a refresh token from frontend state.
 *     The bare `refreshAccessToken()` helper sends an empty body and
 *     relies on the cookie. Refresh failure runs `clearSessionAndCache`
 *     (full TanStack Query wipe + auth-store logout) and redirects to
 *     /login with a safe return target so SessionBootstrap re-runs cleanly.
 *   - 403 still passes through unchanged — never logs out.
 *   - The 401 logic is split into a `handle401` helper so unit tests
 *     can exercise the retry/logout decision tree without a real
 *     network round trip.
 */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { AUTH_I18N_KEYS } from '@invento/shared';
import * as Sentry from '@sentry/react';
import i18n from '@/locales';
import { useAuthStore } from '@/state/stores/auth.store';
import {
  createNotificationScope,
  createScopedNotifications,
  registerNotificationSessionResetter,
  type NotificationScope,
} from '@/core/notifications';
import { refreshAccessToken } from './refresh-client';
import { clearSessionAndCache } from '@/core/auth/clear-session';
import { queryClient } from '@/state/query/query-client';
import { sanitizeBreadcrumbUrl } from '@/lib/sanitize-breadcrumb-url';
import { claimSessionRecovery } from '@/core/auth/session-recovery';
import { redirectBrowserToLogin } from '@/core/auth/session-recovery-redirect';
import { buildLoginRecoveryPath, currentBrowserPath } from '@/navigation/safe-return-target';

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _notificationScope?: NotificationScope;
}

/**
 * Browser-native UUID v4 generator. Available in all modern browsers; falls
 * back to a Math.random()-based UUID only on very old runtimes (this is a
 * correlation ID, not a security token).
 */
function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'req-xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach access token, business id, X-Request-ID, Accept-Language.
apiClient.interceptors.request.use((config) => {
  const retryableConfig = config as RetryableConfig;
  // Capture the identity that started this request. The response interceptor
  // must not announce a late 403 to a different login.
  retryableConfig._notificationScope ??= createNotificationScope();
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const businessId = useAuthStore.getState().user?.businessId;
  if (businessId) {
    config.headers['x-business-id'] = businessId;
  }

  config.headers['X-Request-ID'] = generateRequestId();

  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  config.headers['Accept-Language'] = locale;

  return config;
});

/**
 * Single-flight refresh promise. Multiple parallel 401s share one
 * refresh round trip; whoever wins kicks off the refresh, everyone
 * else awaits the same promise.
 */
let refreshInflight: Promise<string> | null = null;

async function getOrStartRefresh(): Promise<string> {
  if (!refreshInflight) {
    refreshInflight = refreshAccessToken()
      .then(({ accessToken }) => accessToken)
      .finally(() => {
        refreshInflight = null;
      });
  }
  return refreshInflight;
}

interface Handle401Deps {
  client: AxiosInstance;
  refresh: () => Promise<string>;
  setAccessToken: (token: string) => void;
  onSessionInvalid: () => void;
}

const isPublicAuthRequest = (url: string | undefined): boolean => {
  const path = (url ?? '').split('?')[0];
  return (
    path.endsWith('/auth/login') ||
    path.endsWith('/auth/register') ||
    path.includes('/auth/password-reset/')
  );
};

/**
 * Verification challenge failures are authenticated form errors, not
 * session failures. The backend intentionally uses 401 for an invalid or
 * exhausted OTP, so keying this exception off the fixed i18n vocabulary
 * keeps the form error visible without weakening recovery for a session 401
 * from the same route.
 */
const isVerificationChallengeFailure = (error: AxiosError, url: string | undefined): boolean => {
  const path = (url ?? '').split('?')[0];
  const isVerificationConfirmRequest =
    path.endsWith('/auth/verify/email/confirm') ||
    path.endsWith('/auth/verify/phone/confirm');
  if (!isVerificationConfirmRequest) return false;

  const body = error.response?.data as
    | { error?: { i18nKey?: unknown } }
    | undefined;
  const i18nKey = body?.error?.i18nKey;
  return (
    i18nKey === AUTH_I18N_KEYS.VERIFICATION_CODE_INVALID ||
    i18nKey === AUTH_I18N_KEYS.VERIFICATION_TOO_MANY_ATTEMPTS
  );
};

/**
 * Phase 5 — 403 toast surface.
 *
 * Backend authorization is the boundary; the client just renders a
 * single localized "permission required" toast so the user knows
 * the API rejected their action. Critical invariants (per
 * FRONTEND_AUTH_PERMISSIONS.md):
 *
 *   - 403 NEVER triggers /auth/refresh
 *   - 403 NEVER calls clearSessionAndCache()
 *   - 403 NEVER redirects to /login
 *   - The original promise still rejects so calling code can render
 *     page-level errors if it wants to
 *
 * Dedupe: a page commonly fires several parallel requests; collapsing
 * a burst of identical 403s into one toast avoids spam. The dedupe
 * window is intentionally short — 1500ms — so two genuinely separate
 * user actions still each surface a toast. The key is method+URL so
 * 403s on different endpoints within the window each fire once.
 *
 * The toast store has no native stable-key support (verified), so
 * we keep a tiny in-module Map and prune opportunistically on every
 * fire-or-skip decision.
 */
const FORBIDDEN_TOAST_DEDUPE_WINDOW_MS = 1500;
const recentForbiddenToasts = new Map<string, number>();

interface Handle403Deps {
  notifyForbidden: (title: string, message: string, scope?: NotificationScope) => void;
  translate: (key: string) => string;
  now: () => number;
}

function pruneExpired(now: number): void {
  for (const [key, ts] of recentForbiddenToasts) {
    if (now - ts > FORBIDDEN_TOAST_DEDUPE_WINDOW_MS) {
      recentForbiddenToasts.delete(key);
    }
  }
}

/** Test-only — clears the in-module dedupe map between cases. */
export function __resetForbiddenDedupeForTests(): void {
  recentForbiddenToasts.clear();
}

// `clearSessionAndCache` calls this synchronously through the notification
// session boundary. Keeping registration here avoids a core-auth ↔ api cycle.
registerNotificationSessionResetter(__resetForbiddenDedupeForTests);

/**
 * Phase 6 — Sentry breadcrumb for failed API responses.
 *
 * Records every 4xx/5xx response as a Sentry breadcrumb so the
 * eventual error capture has the network-request lead-up. The
 * breadcrumb data is intentionally tiny and PII-free:
 *
 *   - method:    HTTP verb
 *   - route:     sanitizeBreadcrumbUrl(config.url) — query string,
 *                hash, and opaque ID segments stripped
 *   - status:    HTTP response status
 *   - errorCode: backend AppError code if present
 *   - requestId: backend X-Request-ID for cross-system correlation
 *   - durationMs: rough request duration when measurable
 *
 * NEVER includes: request body, response body, Authorization header,
 * cookies, raw URL, raw query string, raw user agent.
 *
 * 2xx successes are NOT recorded as breadcrumbs (would just be noise).
 */
export function recordFailureBreadcrumb(error: AxiosError): void {
  const status = error.response?.status;
  if (status === undefined || status < 400) return;

  const config = error.config;
  const method = (config?.method ?? 'get').toUpperCase();
  const route = sanitizeBreadcrumbUrl(config?.url);
  const responseHeaders = error.response?.headers as
    | Record<string, string | undefined>
    | undefined;
  const responseData = error.response?.data as
    | { error?: { code?: string; requestId?: string } }
    | undefined;

  Sentry.addBreadcrumb({
    category: 'http',
    type: 'error',
    level: status >= 500 ? 'error' : 'warning',
    data: {
      method,
      route,
      status,
      errorCode: responseData?.error?.code ?? null,
      requestId:
        responseData?.error?.requestId ??
        responseHeaders?.['x-request-id'] ??
        null,
    },
  });
}

/**
 * Records only the fixed recovery outcome. The current route is deliberately
 * excluded because it can contain customer identifiers or query data.
 */
export function recordSessionRecoveryBreadcrumb(): void {
  Sentry.addBreadcrumb({
    category: 'auth.recovery',
    type: 'info',
    level: 'warning',
    data: {
      reason: 'session-expired',
      action: 'sign-in-again',
    },
  });
}

interface SessionInvalidationDeps {
  clearSession: () => void;
  recordRecovery: () => void;
  redirectToLogin: () => void;
}

/**
 * Single recovery path for every failed refresh. Keeping the claim around
 * the complete side-effect sequence prevents concurrent 401s from clearing
 * state, emitting diagnostics, or redirecting more than once.
 */
export function handleSessionInvalidation(deps: SessionInvalidationDeps): boolean {
  if (!claimSessionRecovery()) return false;
  deps.clearSession();
  deps.recordRecovery();
  deps.redirectToLogin();
  return true;
}

export function handle403(error: AxiosError, deps: Handle403Deps): void {
  if (error.response?.status !== 403) return;

  const config = error.config;
  const method = (config?.method ?? 'get').toUpperCase();
  const url = config?.url ?? '';
  const scope = (config as RetryableConfig | undefined)?._notificationScope;
  // The route remains only an in-memory dedupe key; it is never used as a
  // toast ID or rendered. The epoch makes dedupe deterministic per identity.
  const key = `${scope?.epoch ?? 0}:${method} ${url}`;

  const now = deps.now();
  pruneExpired(now);

  const lastFired = recentForbiddenToasts.get(key);
  if (lastFired !== undefined && now - lastFired <= FORBIDDEN_TOAST_DEDUPE_WINDOW_MS) {
    return;
  }
  recentForbiddenToasts.set(key, now);

  const title = deps.translate('errors.forbidden.toast.title');
  const message = deps.translate('errors.forbidden.toast.message');
  if (scope) {
    deps.notifyForbidden(title, message, scope);
  } else {
    deps.notifyForbidden(title, message);
  }
}

/**
 * 401 decision tree, exposed for unit tests. The wiring at the bottom
 * of the file binds the deps to real collaborators.
 */
export async function handle401(
  error: AxiosError,
  deps: Handle401Deps,
): Promise<unknown> {
  const config = error.config as RetryableConfig | undefined;

  if (error.response?.status !== 401 || !config) {
    return Promise.reject(error);
  }

  // Never refresh a refresh — that would recurse and mask the real
  // failure mode. Treat a 401 from /auth/refresh as session-invalid.
  if (config.url?.includes('/auth/refresh')) {
    deps.onSessionInvalid();
    return Promise.reject(error);
  }

  // Expected credential/reset failures belong to their public forms.
  // Refreshing here would turn a useful form error into a hard redirect
  // and could erase localized validation copy before it is rendered.
  if (isPublicAuthRequest(config.url)) {
    return Promise.reject(error);
  }

  if (isVerificationChallengeFailure(error, config.url)) {
    return Promise.reject(error);
  }

  if (config._retry) {
    // A refreshed access token was still rejected. Do not refresh again;
    // converge on the same single recovery path as any other invalid session.
    deps.onSessionInvalid();
    return Promise.reject(error);
  }
  config._retry = true;

  let newToken: string;
  try {
    newToken = await deps.refresh();
  } catch {
    deps.onSessionInvalid();
    return Promise.reject(error);
  }

  deps.setAccessToken(newToken);
  config.headers.Authorization = `Bearer ${newToken}`;
  return deps.client.request(config);
}

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Phase 6 — record a Sentry breadcrumb for any failed (>=400)
    // response. Sanitized route only; never raw URLs / bodies /
    // headers / cookies. Runs before 401/403 handling so the
    // breadcrumb is captured even if a downstream handler
    // re-issues or transforms the request.
    recordFailureBreadcrumb(error);

    // 403: localized toast + dedupe; never refresh/logout/redirect.
    // Fires before the 401 path because the 401 path only acts on
    // status === 401, so the order doesn't actually overlap, but
    // putting it first keeps the intent obvious.
    handle403(error, {
      notifyForbidden: (title, message, scope) =>
        createScopedNotifications(scope ?? createNotificationScope()).error(title, message, {
          // Deliberately generic: no route, server message, or backend error
          // code can enter a toast identifier.
          id: 'forbidden',
        }),
      translate: (key) => i18n.t(key, { ns: 'common' }),
      now: () => Date.now(),
    });
    return handle401(error, {
      client: apiClient,
      refresh: getOrStartRefresh,
      setAccessToken: (token) => useAuthStore.getState().setAccessToken(token),
      onSessionInvalid: () =>
        handleSessionInvalidation({
          clearSession: () => clearSessionAndCache({ queryClient }),
          recordRecovery: recordSessionRecoveryBreadcrumb,
          redirectToLogin: () => {
            // Hard reload so SessionBootstrap re-runs from a clean slate.
            redirectBrowserToLogin(
              buildLoginRecoveryPath({
                returnTo: currentBrowserPath(),
                reason: 'session-expired',
              }),
            );
          },
        }),
    });
  },
);

export type ApiClientRequestConfig = AxiosRequestConfig;
